import { datetime, todayIso } from '@common/services/datetime';
import type {
  LoadBalancingScheduleShiftPreference,
  LoadBalancingShiftPatternPreference,
  SaveWorkerProfileChangeMutationVariables,
} from '@common/graphql';
import type { LoadBalancingWorkerTermType } from '@load-balancing/graphql/queries/load-balancing-worker';
import {
  pickCurrentTerm,
  shiftPreferenceLabel,
} from '@load-balancing/services';

export function buildRoleOptions(
  assignableRoles: readonly string[]
): { value: string; label: string }[] {
  return assignableRoles.map((role) => ({ value: role, label: role }));
}

export const SHIFT_TYPE_OPTIONS: {
  value: LoadBalancingScheduleShiftPreference;
  label: string;
}[] = (['DAY', 'NIGHT', 'EITHER'] as const).map((value) => ({
  value,
  label: shiftPreferenceLabel(value),
}));

export const SHIFT_PATTERN_OPTIONS: {
  value: LoadBalancingShiftPatternPreference;
  label: string;
}[] = [
  { value: 'CONSECUTIVE', label: 'Consecutive' },
  { value: 'NON_CONSECUTIVE', label: 'Non-consecutive' },
  { value: 'NO_PREFERENCE', label: 'No Shift Pattern' },
];

export type WorkerProfileFormValues = {
  effectiveDate: string;
  endDate: string;
  accountActive: boolean;
  role: string;
  ftePercentage: number | null;
  chargeEligible: boolean;
  preferredTime: LoadBalancingScheduleShiftPreference;
  shiftPatternPreference: LoadBalancingShiftPatternPreference;
  addedSkillUuids: string[];
  removedSkillUuids: string[];
};

export const ISO_DATE = 'YYYY-MM-DD';

export const SKILLS_TEMPORARY_COPY =
  "Skill changes can't be temporary yet. Save skills in a change with no end date.";

export const SKILLS_NOT_LOADED_COPY =
  'Could not load skills for this date, so the change was not saved.';

function startOfToday() {
  return datetime().startOf('day');
}

export function isPast(iso: string): boolean {
  return !!iso && datetime(iso).isBefore(startOfToday(), 'day');
}

// NOTE: ISO 'YYYY-MM-DD' strings sort chronologically, so comparing these dates lexically
// is correct.
export function isEndBeforeStart(
  effectiveDate: string,
  endDate: string
): boolean {
  return !!effectiveDate && !!endDate && endDate < effectiveDate;
}

export function laterChanges(
  terms: readonly LoadBalancingWorkerTermType[],
  effectiveDate: string
): LoadBalancingWorkerTermType[] {
  if (!effectiveDate) return [];

  return terms.filter((term) => term.effectiveStartDate > effectiveDate);
}

export function shownSkillUuids({
  loaded,
  added,
  removed,
}: {
  loaded: readonly string[] | null;
  added: readonly string[];
  removed: readonly string[];
}): string[] {
  if (loaded === null) return [];

  return [...new Set([...loaded, ...added])].filter(
    (uuid) => !removed.includes(uuid)
  );
}

export function skillEditFromSelection({
  loaded,
  selected,
  added,
  removed,
}: {
  loaded: readonly string[];
  selected: readonly string[];
  added: readonly string[];
  removed: readonly string[];
}): Pick<WorkerProfileFormValues, 'addedSkillUuids' | 'removedSkillUuids'> {
  const newlyAdded = selected.filter((uuid) => !loaded.includes(uuid));
  const keptAdded = added.filter((uuid) => selected.includes(uuid));
  const newlyRemoved = loaded.filter((uuid) => !selected.includes(uuid));
  const keptRemoved = removed.filter((uuid) => !selected.includes(uuid));

  return {
    addedSkillUuids: [...new Set([...newlyAdded, ...keptAdded])],
    removedSkillUuids: [...new Set([...newlyRemoved, ...keptRemoved])],
  };
}

export function skillUuidsToSave({
  values,
  loaded,
  skillsEnabled,
  skillsLocked,
}: {
  values: WorkerProfileFormValues;
  loaded: readonly string[] | null;
  skillsEnabled: boolean;
  skillsLocked: boolean;
}): string[] | null {
  const unchanged =
    values.addedSkillUuids.length === 0 &&
    values.removedSkillUuids.length === 0;
  if (!skillsEnabled || skillsLocked || loaded === null || unchanged) {
    return null;
  }

  return shownSkillUuids({
    loaded,
    added: values.addedSkillUuids,
    removed: values.removedSkillUuids,
  });
}

export function skillEditsAwaitingLoad({
  values,
  loaded,
  skillsEnabled,
  skillsLocked,
}: {
  values: WorkerProfileFormValues;
  loaded: readonly string[] | null;
  skillsEnabled: boolean;
  skillsLocked: boolean;
}): boolean {
  const edited =
    values.addedSkillUuids.length > 0 || values.removedSkillUuids.length > 0;

  return skillsEnabled && !skillsLocked && edited && loaded === null;
}

export function skillsLockedByEndDate(
  endDate: string,
  initialEndDate: string
): boolean {
  return !!endDate && endDate !== initialEndDate;
}

export function initialFormValues({
  terms,
  asOf,
  editingTermId,
}: {
  terms: readonly LoadBalancingWorkerTermType[];
  asOf: string;
  editingTermId?: string | null;
}): WorkerProfileFormValues {
  const editing = editingTermId
    ? terms.find((term) => term.id === editingTermId)
    : undefined;
  const base = editing ?? pickCurrentTerm(terms, asOf);

  return {
    effectiveDate: editing?.effectiveStartDate ?? todayIso(),
    endDate: editing?.effectiveEndDate ?? '',
    accountActive: base?.active ?? true,
    role: base?.role ?? '',
    ftePercentage: base?.ftePercentage ?? null,
    chargeEligible: base?.chargeEligible ?? false,
    preferredTime: base?.preferredTime ?? 'EITHER',
    shiftPatternPreference: base?.shiftPatternPreference ?? 'NO_PREFERENCE',
    addedSkillUuids: [],
    removedSkillUuids: [],
  };
}

export function buildSaveVariables({
  values,
  workerId,
  scheduleGroupId,
  editingTermId,
  schedulingPeriodId,
  skillsEnabled = false,
  skillsLocked = false,
  loadedSkillUuids = null,
}: {
  values: WorkerProfileFormValues;
  workerId: string;
  scheduleGroupId: string;
  editingTermId?: string | null;
  schedulingPeriodId?: string | null;
  skillsEnabled?: boolean;
  skillsLocked?: boolean;
  loadedSkillUuids?: readonly string[] | null;
}): SaveWorkerProfileChangeMutationVariables {
  return {
    workerId,
    scheduleGroupId,
    termId: editingTermId ?? null,
    effectiveStartDate: values.effectiveDate || null,
    effectiveEndDate: values.endDate || null,
    accountActive: values.accountActive,
    role: values.role,
    ftePercentage: values.ftePercentage,
    chargeEligible: values.chargeEligible,
    preferredTime: values.preferredTime,
    shiftPatternPreference: values.shiftPatternPreference,
    schedulingPeriodId: schedulingPeriodId ?? null,
    skillUuids: skillUuidsToSave({
      values,
      loaded: loadedSkillUuids,
      skillsEnabled,
      skillsLocked,
    }),
  };
}
